/**
 * SearchDataGrid — таблица enriched_data после search_enrichment.
 *
 * P1.3 (2026-08-14): мигрирована на единый `<DataTable>` — каркас
 * (pagination/pin/sort/filter/auto-scroll) делегирован; здесь только
 * column-def'ы + cell-рендеры + открытие detail drawer.
 *
 * UX-J7..J10 (docs/UX_PROCESS_SHELL_AND_RESULTS_V1.md § 5):
 * - 5 видимых колонок: # / Identity / Status / Summary / Confidence.
* - M2-A (2026-09-14): колонка Sources удалена — источники не выводятся
*   в основной таблице (проверяемость — во вкладке «Детали» карточки).
 * - Internal/meta колонки (raw_data, generated_content, error_message длинный)
 *   скрыты из грида — открываются в detail drawer.
 * - v2 status mapping (UX-J8):
 *     enriched    → ok      (emerald)
 *     partial     → partial (amber)
 *     not_found   → not_found (gray)
 *     blocked / error → failed (rose)
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';

import { usePipelineRows, usePipelineStatus, type PipelineRow } from '@/hooks/useGeneration';
import { useSearchStore } from '@/store/searchStore';
import type { SearchRowResult } from '@/hooks/useSearchResults';
import { DetailViewModal } from '@/components/generation/DetailViewModal';
import { DataTable } from '@/components/shared/DataTable';
import { cn } from '@/lib/utils';

// === UX-J8: internal v2 status → user-facing label/class ===
type UserFacingStatus = 'ok' | 'partial' | 'not_found' | 'failed' | 'pending' | 'limit_wait';

const STATUS_MAP: Record<string, { label: UserFacingStatus; className: string }> = {
  enriched: { label: 'ok', className: 'bg-success-tint/15 text-success' },
  partial: { label: 'partial', className: 'bg-warning-tint/15 text-warning' },
  not_found: { label: 'not_found', className: 'bg-surface-700/40 text-surface-300' },
  limit_wait: { label: 'limit_wait', className: 'bg-warning-tint/15 text-warning' },
  blocked: { label: 'failed', className: 'bg-danger-tint/15 text-danger' },
  error: { label: 'failed', className: 'bg-danger-tint/15 text-danger' },
};

const PENDING_STYLE = 'bg-surface-700/40 text-surface-400';

// === Internal column helpers ===

// Fix 2026-09-18 (дефект №4): имя ищется по СТЕМАМ ключей raw_data — колонка
// прайса может называться «Найменування продукту» / «Название товара» / с
// любым хвостом; точное совпадение ключа ловит только частные случаи.
// Код (SKU/артикул, цифры без букв) именем не считается — если имени нет,
// показывается «Название не определено» (noNameLabel), а не код.
const _NAME_KEY_STEMS = [
  'найменув', 'назв', 'наименован', 'name', 'title', 'назва',
];
const _SKU_KEY_STEMS = ['sku', 'артикул', 'арт', 'код', 'code', 'mpn', 'vendor'];

const _normKey = (k: string): string =>
  k.toLowerCase().replace(/[\s\-.:,;()"']+|_/g, '').trim();

const _hasLetters = (v: string): boolean => /\p{L}/u.test(v);

function rawNameFromData(rawData: unknown): string | undefined {
  if (!rawData || typeof rawData !== 'object') return undefined;
  const rd = rawData as Record<string, unknown>;
  // приоритет: точные ключи → стемы; кодовые ключи (SKU) не рассматриваются.
  const entries = Object.entries(rd);
  const exact = entries.find(
    ([k, v]) =>
      _NAME_KEY_STEMS.includes(_normKey(k)) && typeof v === 'string' && v.trim() !== '',
  );
  if (exact) return (exact[1] as string).trim();
  const stemmed = entries.find(
    ([k, v]) =>
      typeof v === 'string' &&
      v.trim() !== '' &&
      _hasLetters(v as string) &&
      !_SKU_KEY_STEMS.some((s) => _normKey(k).includes(s)) &&
      _NAME_KEY_STEMS.some((s) => _normKey(k).includes(s)),
  );
  if (stemmed) return (stemmed[1] as string).trim();
  return undefined;
}

function extractIdentity(row: PipelineRow, noNameLabel?: string): string {
  // Приоритет 1: имя из исходных данных (стем-матч) — источник правды о названии.
  const rawName = rawNameFromData(row.raw_data);
  if (rawName) return rawName;
  // Приоритет 2: identity из enriched_data, НО только значения-имена.
  // «31980» (identity.model старых прогонов = код) именем не считается.
  const ed = row.enriched_data as
    | { identity?: { canonical_name?: string; name?: string; brand?: string; model?: string; part_number?: string } }
    | null;
  const ident = ed?.identity;
  const identName = [ident?.canonical_name, ident?.name]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .find((v) => v !== '' && _hasLetters(v));
  if (identName) return identName;
  const brandModel = [ident?.brand, ident?.model]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((v) => v !== '' && _hasLetters(v))
    .join(' ')
    .trim();
  if (brandModel) return brandModel;
  // Fix 2026-09-18: имени нет — показываем «Название не определено», не код
  // (part_number/SKU в identity именем строки не является).
  return noNameLabel ?? '';
}

// M1 (2026-09-10): уверенность — только результат-уровень (средняя по
// реально проверенным страницам). identity.confidence — планировщик,
// не факт-проверка: не показываем.
function extractConfidence(row: PipelineRow): number | null {
  const ed = row.enriched_data as { confidence?: number | null } | null;
  return typeof ed?.confidence === 'number' ? ed.confidence : null;
}

function extractSummary(row: PipelineRow): string {
  const ed = row.enriched_data as
    | {
        summary?: string;
        // Шаг 1 (2026-09-07): синтезированный результат — приоритетная сводка
        // (конфликты разрешены, значения переведены).
        synthesized_facts?: Record<string, string> | null;
        facts?: { key: string; value: string }[];
      }
    | null;
  if (ed?.synthesized_facts && Object.keys(ed.synthesized_facts).length > 0) {
    return Object.entries(ed.synthesized_facts)
      .slice(0, 3)
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ');
  }
  if (ed?.summary) return ed.summary;
  if (Array.isArray(ed?.facts) && ed.facts.length > 0) {
    return ed.facts
      .slice(0, 3)
      .map((f) => `${f.key}: ${f.value}`)
      .join(' · ');
  }
  return '';
}

// TZ 2026-09-20 (test preview): сводка из display-запуска (truth.summary) —
// имя / SKU / бренд + число подтверждённых фактов. Появляется сразу после
// тестового прогона, а не только после полного.
function extractTruthSummary(
  truth: SearchRowResult | undefined,
): string {
  const s = truth?.summary;
  if (!s) return '';
  const parts: string[] = [];
  if (s.title) parts.push(s.title);
  if (s.sku) parts.push(`SKU: ${s.sku}`);
  if (s.brand) parts.push(s.brand);
  if (parts.length === 0 && s.facts_count > 0) {
    return `${s.facts_count}`;
  }
  return parts.join(' · ');
}

// M2-A: возвращает ключ статуса ('ok'|'partial'|'not_found'|'failed'|
// pending|row.status) — подпись локализуется t('search.status.<key>').
function resolveStatusLabel(row: PipelineRow): string {
  const ed = row.enriched_data as { schema_version?: number; status?: string } | null;
  const v2Status = ed?.schema_version === 2 ? ed?.status : null;
  if (!v2Status) return row.status === 'success' ? 'ok' : row.status;
  return (STATUS_MAP[v2Status] ?? { label: v2Status as UserFacingStatus }).label;
}

export function SearchDataGrid({
  resultsById,
  selectedRowIds,
  onSelectedRowIdsChange,
  scopeRowIds,
  runActive,
  runShortId,
}: {
  /** M2-B: truth-результаты по row_id (база истины); optional — legacy-путь работает как раньше. */
  resultsById?: Map<string, SearchRowResult>;
  /** M2-C: выбранные для rerun строки (Set row_id) — состояние держит родитель. */
  selectedRowIds?: Set<string>;
  /** M2-C: callback при изменении выбора строк. */
  onSelectedRowIdsChange?: (next: Set<string>) => void;
  // Fix 2026-09-19 (видимость запуска, ТЗ §A2): строки scope текущего прогона
  // (scope_row_ids из API) — подсветка строки в колонках # и Identity.
  scopeRowIds?: Set<string>;
  runActive?: boolean;
  runShortId?: string | null;
}) {
  const { t } = useTranslation();
  const currentPipelineId = useSearchStore((s) => s.currentPipelineId);
  const [selectedRow, setSelectedRow] = useState<PipelineRow | null>(null);

  // M1 (2026-09-10): пока прогон active — опрос строк каждые 2s.
  const { data: pipeline } = usePipelineStatus(currentPipelineId);
  const pipelineActive =
    pipeline?.status === 'running' ||
    pipeline?.status === 'queued' ||
    pipeline?.status === 'paused';

  // Fix 2026-09-18: include_unprocessed=true — грид показывает ВСЕ строки
  // датасета (в т.ч. не вошедшие в scope test run'а), а не только обработанные;
  // непрогнанные строки помечает «Не запускался» по truth-статусу.
  const { data, isLoading } = usePipelineRows(
    currentPipelineId,
    200,
    {
      refetchInterval: pipelineActive ? 2_000 : false,
    },
    true,
  );
  const rows = data?.rows ?? [];

  // M1: immediate pending — пока прогон ещё не отдал строки, показываем
  // заглушки по total_rows, чтобы таблица не выглядела пустой.
  const pendingCount = pipelineActive
    ? Math.max(0, (pipeline?.total_rows ?? 0) - rows.length)
    : 0;
  const displayRows = useMemo<PipelineRow[]>(() => {
    if (pendingCount === 0) return rows;
    const placeholders: PipelineRow[] = Array.from({ length: pendingCount }, (_, i) => ({
      id: `pending-${i}`,
      row_index: -1,
      status: 'pending',
      generated_content: null,
      enriched_data: null,
      raw_data: {},
      error_message: null,
      spent_credits: '0',
    }));
    return [...rows, ...placeholders];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, pendingCount]);

  // M2-C: доступные для выбора строки — только реальные строки (не pending-заглушки).
  const selectableIds = useMemo(
    () => rows.map((r) => r.id),
    [rows],
  );
  const toggleRow = (next: (cur: Set<string>) => Set<string>) => {
    const cur = selectedRowIds ?? new Set<string>();
    onSelectedRowIdsChange?.(next(cur));
  };
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedRowIds?.has(id));
  const someSelected = !allSelected && selectableIds.some((id) => selectedRowIds?.has(id));

  const columns = useMemo<ColumnDef<PipelineRow>[]>(() => {
    return [
      // 0. M2-C: чекбокс выбора строки для rerun
      {
        id: 'rerun-select',
        header: () => (
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary-500"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            onChange={() =>
              toggleRow((cur) =>
                allSelected || cur.size > 0 ? new Set<string>() : new Set(selectableIds),
              )
            }
            aria-label={t('search.rerun.selectAll', { defaultValue: 'Select all rows' })}
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          // Fix 2026-09-18: непрогнанная строка (pending, вне scope прогона)
          // для rerun не выбирается.
          const isPending = row.row_index < 0 || row.status === 'pending';
          return (
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary-500"
                checked={selectedRowIds?.has(row.id) ?? false}
                disabled={isPending}
                onChange={() =>
                  toggleRow((cur) => {
                    const next = new Set(cur);
                    if (next.has(row.id)) next.delete(row.id);
                    else next.add(row.id);
                    return next;
                  })
                }
                aria-label={t('search.rerun.selectRow', { defaultValue: 'Select row' })}
              />
            </div>
          );
        },
        size: 40,
        enableColumnFilter: false,
        enableSorting: false,
      },
      // 1. # (row_index)
      {
        accessorKey: 'row_index',
        header: '#',
        cell: (info) => {
          const idx = info.getValue<number>();
          const row = info.row.original;
          // Fix 2026-09-19 (ТЗ §A2): строка входит в scope текущего запуска —
          // точка-маркер с человекочитаемым tooltip (без сырых UUID).
          const inScope = scopeRowIds?.has(row.id) ?? false;
          return (
            <span className="inline-flex items-center gap-1">
              <span className="text-surface-500 font-mono text-xs">
                {idx < 0 ? '—' : `#${idx}`}
              </span>
              {inScope && (
                <span
                  data-testid="row-in-scope"
                  className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    runActive ? 'bg-primary-400 animate-pulse' : 'bg-primary-500/70',
                  )}
                  title={
                    runActive
                      ? t('search.run.rowInCurrentRun', {
                          defaultValue: 'In current run',
                        })
                      : t('search.run.rowProcessedInRun', {
                          shortId: runShortId ?? '',
                          defaultValue: `Processed in run #${runShortId ?? ''}`,
                        })
                  }
                />
              )}
            </span>
          );
        },
        size: 60,
        enableColumnFilter: false,
      },
      // 2. Identity
      {
        id: 'identity',
        accessorFn: (row) =>
          extractIdentity(
            row,
            t('search.grid.noName', { defaultValue: 'Name not defined' }),
          ),
        header: t('search.grid.identity', { defaultValue: 'Identity' }),
        cell: (info) => {
          const name = extractIdentity(
            info.row.original,
            t('search.grid.noName', { defaultValue: 'Name not defined' }),
          );
          // Fix 2026-09-19 (ТЗ §A2): визуальная подсветка строк текущего
          // запуска — левая рамка + лёгкий тон на Identity.
          const inScope = scopeRowIds?.has(info.row.original.id) ?? false;
          return (
            <div
              className={cn(
                'text-sm text-surface-100 max-w-[20rem] truncate',
                inScope && 'border-l-2 border-primary-400 pl-2 bg-primary-500/5',
              )}
              title={name}
            >
              {name || <span className="text-surface-500">—</span>}
            </div>
          );
        },
        size: 280,
      },
      // 3. Status
      {
        id: 'status',
        accessorFn: (row) => resolveStatusLabel(row),
        header: t('search.grid.status', { defaultValue: 'Status' }),
        cell: (info) => {
          const row = info.row.original;
          // M2-B: truth-статус из базы истины имеет приоритет над enriched_data.
          const truth = resultsById?.get(row.id);
          if (truth) {
            // TZ 2026-09-20 (ТЗ §5): статусы только из display-разрешения API
            // (current_display_result_kind). Текстов «Результат не сохранён»
            // больше нет: до запуска — «Не запускался», после тест-прогона —
            // «Тестовый результат», после полного прогона — финальный статус.
            if (truth.current_display_result_kind === 'test') {
              return (
                <span
                  className="text-xs px-1.5 py-0.5 rounded uppercase tracking-wider bg-info-tint/15 text-info whitespace-nowrap cursor-help"
                  title={t('search.status.testResultTooltip', {
                    defaultValue: 'Preview of the test run',
                  })}
                >
                  {t('search.status.testResult', {
                    defaultValue: 'Test result',
                  })}
                </span>
              );
            }
            if (!truth.status) {
              return (
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider',
                    PENDING_STYLE,
                  )}
                >
                  {t('search.grid.notRun', { defaultValue: 'Not run' })}
                </span>
              );
            }
            const tMapped = STATUS_MAP[truth.status] ?? {
              label: truth.status as UserFacingStatus,
              className: PENDING_STYLE,
            };
            return (
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider',
                  tMapped.className,
                )}
              >
                {t(`search.status.${truth.status}`, { defaultValue: tMapped.label })}
              </span>
            );
          }
          const ed = row.enriched_data as { schema_version?: number; status?: string } | null;
          const v2Status = ed?.schema_version === 2 ? ed?.status : null;
          if (!v2Status) {
            return (
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider',
                  row.status === 'success'
                    ? 'bg-success-tint/15 text-success'
                    : row.status === 'error'
                      ? 'bg-danger-tint/15 text-danger'
                      : PENDING_STYLE,
                )}
              >
                {t(
                  `search.status.${row.status === 'success' ? 'ok' : row.status}`,
                  { defaultValue: row.status === 'success' ? 'ok' : row.status },
                )}
              </span>
            );
          }
          const mapped = STATUS_MAP[v2Status] ?? {
            label: v2Status as UserFacingStatus,
            className: PENDING_STYLE,
          };
          return (
            <span
              className={cn(
                'text-xs px-1.5 py-0.5 rounded uppercase tracking-wider',
                mapped.className,
              )}
            >
              {t(`search.status.${v2Status}`, { defaultValue: mapped.label })}
            </span>
          );
        },
        size: 100,
      },
      // 4. Summary
      {
        id: 'summary',
        accessorFn: (row) => {
          const truth = resultsById?.get(row.id);
          return extractTruthSummary(truth) || extractSummary(row);
        },
        header: t('search.grid.summary', { defaultValue: 'Summary' }),
        cell: (info) => {
          const truth = resultsById?.get(info.row.original.id);
          const text = extractTruthSummary(truth) || extractSummary(info.row.original);
          const truncated = text.length > 160 ? text.slice(0, 160) + '…' : text;
          return (
            <div className="text-sm text-surface-300 max-w-md truncate" title={text}>
              {truncated || <span className="text-surface-500">—</span>}
            </div>
          );
        },
        size: 380,
        enableColumnFilter: false,
      },
      // 5. Confidence
      {
        id: 'confidence',
        accessorFn: (row) =>
          resultsById?.get(row.id)?.confidence ?? extractConfidence(row),
        header: t('search.grid.confidence', { defaultValue: 'Confidence' }),
        cell: (info) => {
          const row = info.row.original;
          const truth = resultsById?.get(row.id);
          // Fix 2026-09-18 (дефект №3): если есть truth-запись — уверенность
          // ТОЛЬКО из базы истины (null → «—»). enriched_data тест-прогона
          // (confidence 0.0 без сохранённого результата) не подставляется.
          const conf = truth ? truth.confidence : extractConfidence(row);
          if (conf === null || conf === undefined) {
            return <span className="text-xs text-surface-500">—</span>;
          }
          const pct = Math.round(conf * 100);
          const color =
            pct >= 80
              ? 'text-success'
              : pct >= 50
                ? 'text-warning'
                : 'text-danger';
          return (
            <div className="flex items-center gap-2">
              <div className="w-12 h-1.5 bg-surface-800 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full',
                    pct >= 80
                      ? 'bg-success-tint'
                      : pct >= 50
                        ? 'bg-warning-tint'
                        : 'bg-danger-tint',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className={cn('text-xs font-mono tabular-nums', color)}>{pct}%</span>
            </div>
          );
        },
        size: 130,
        enableColumnFilter: false,
      },
    ];
    // M2-C: selectedRowIds/selectedRowIdsChange — состояние выбора; результатыById
    // читаются в замыкании (existing pattern, eslint-disable ниже не нужен —
    // deps уже шире дефолта).
    // Fix 2026-09-19 (ТЗ §A2): scope-подсветка в cell-рендерах замыкается
    // на scopeRowIds/runActive/runShortId — должны быть в deps.
  }, [t, allSelected, someSelected, selectedRowIds, selectableIds, onSelectedRowIdsChange, scopeRowIds, runActive, runShortId]);

  if (!currentPipelineId) {
    return (
      <div className="card text-sm text-surface-500">
        {t('search.results.empty', { defaultValue: 'Start search to see enriched data.' })}
      </div>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={displayRows}
        isLoading={isLoading}
        pinLeftCount={1}
        onRowClick={(row) => setSelectedRow(row)}
        emptyState={t('search.results.noRows', { defaultValue: 'No rows yet.' })}
      />
      {selectedRow && (
        <DetailViewModal
          row={selectedRow}
          legacyResult={Boolean(
            selectedRow &&
              resultsById?.get(selectedRow.id)?.legacy_result &&
              !resultsById.get(selectedRow.id)?.status,
          )}
          truthResult={resultsById?.get(selectedRow.id) ?? null}
          showRawData={false}
          showGeneratedContent={false}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </>
  );
}